import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Drawer,
  FormControl,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Checkbox,
  ListItemText,
  ListSubheader,
  Alert,
  Snackbar,
  Divider,
  Grid,
  Avatar,
  useTheme,
  InputAdornment,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  ManageAccounts as ManageAccountsIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  FactCheck as FactCheckIcon,
  ContentCopy as ContentCopyIcon,
  Business as BusinessIcon,
  Hotel as HotelIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  Badge as BadgeIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import DashboardHeader from '../common/DashboardHeader';
import { getUserProfile } from '../../services/api';
import DataTable from '../../shared/components/data/DataTable';
import {
  DrawerHeaderTitle,
  getDrawerCancelButtonSx,
  getDrawerPrimaryButtonSx,
  getSoftFieldSx,
} from '../housekeeping/housekeepingFormKit';
import {
  getHousekeepingDrawerFooterSx,
  getHousekeepingDrawerHeaderSx,
  getHousekeepingDrawerPaperSx,
  getHousekeepingThemeColors,
} from '../../utils/housekeepingMobileLayout';
import { createDrawerOverlayProps, getDrawerRootSx } from '../../utils/drawerOverlayProps';
import { formatHotelDateTime, getHotelTimezone } from '../../utils/dateUtils';
import { isPermissionRelevantForHotel } from '../../utils/featurePermissions';
import { PERMISSIONS, getPermissionDisplayName, getPermissionGroupName } from '../../auth/permissions';
import axios from 'axios';
import moment from 'moment';

const THEME_COLOR = '#4ce1b6';
const DEFAULT_AUDIT_PER_PAGE = 20;
const USERS_FETCH_LIMIT = 500;
const OWNER_ROLE_SUPPORT_MESSAGE = 'Owner role is protected. To remove or change Owner access, please contact RoomNexa Support.';
const HIDDEN_ROLE_NAMES = new Set(['front desk', 'receptionist']);

/** Distinct chip colors per seeded role (dark / light). */
const ROLE_CHIP_PALETTE = {
  owner: {
    dark: { color: '#fde68a', bgcolor: 'rgba(245, 158, 11, 0.22)', border: 'rgba(245, 158, 11, 0.55)' },
    light: { color: '#92400e', bgcolor: 'rgba(251, 191, 36, 0.22)', border: 'rgba(217, 119, 6, 0.45)' },
  },
  'general manager': {
    dark: { color: '#6ee7b7', bgcolor: 'rgba(16, 185, 129, 0.22)', border: 'rgba(52, 211, 153, 0.55)' },
    light: { color: '#065f46', bgcolor: 'rgba(16, 185, 129, 0.16)', border: 'rgba(5, 150, 105, 0.4)' },
  },
  manager: {
    dark: { color: '#93c5fd', bgcolor: 'rgba(59, 130, 246, 0.22)', border: 'rgba(96, 165, 250, 0.55)' },
    light: { color: '#1e40af', bgcolor: 'rgba(59, 130, 246, 0.14)', border: 'rgba(37, 99, 235, 0.4)' },
  },
  'front office': {
    dark: { color: '#67e8f9', bgcolor: 'rgba(6, 182, 212, 0.22)', border: 'rgba(34, 211, 238, 0.55)' },
    light: { color: '#155e75', bgcolor: 'rgba(6, 182, 212, 0.14)', border: 'rgba(8, 145, 178, 0.4)' },
  },
  housekeeping: {
    dark: { color: '#fda4af', bgcolor: 'rgba(244, 63, 94, 0.2)', border: 'rgba(251, 113, 133, 0.5)' },
    light: { color: '#9f1239', bgcolor: 'rgba(244, 63, 94, 0.12)', border: 'rgba(225, 29, 72, 0.35)' },
  },
  restaurant: {
    dark: { color: '#fdba74', bgcolor: 'rgba(249, 115, 22, 0.22)', border: 'rgba(251, 146, 60, 0.55)' },
    light: { color: '#9a3412', bgcolor: 'rgba(249, 115, 22, 0.14)', border: 'rgba(234, 88, 12, 0.4)' },
  },
  'stock inventory': {
    dark: { color: '#a3e635', bgcolor: 'rgba(132, 204, 22, 0.2)', border: 'rgba(163, 230, 53, 0.5)' },
    light: { color: '#3f6212', bgcolor: 'rgba(132, 204, 22, 0.16)', border: 'rgba(101, 163, 13, 0.4)' },
  },
  hall: {
    dark: { color: '#6ee7b7', bgcolor: 'rgba(76, 225, 182, 0.2)', border: 'rgba(76, 225, 182, 0.55)' },
    light: { color: '#0f766e', bgcolor: 'rgba(76, 225, 182, 0.16)', border: 'rgba(15, 118, 110, 0.4)' },
  },
  'revenue manager': {
    dark: { color: '#5eead4', bgcolor: 'rgba(20, 184, 166, 0.22)', border: 'rgba(45, 212, 191, 0.55)' },
    light: { color: '#115e59', bgcolor: 'rgba(20, 184, 166, 0.14)', border: 'rgba(13, 148, 136, 0.4)' },
  },
};
const ROLE_CHIP_FALLBACK = {
  dark: { color: '#e2e8f0', bgcolor: 'rgba(148, 163, 184, 0.16)', border: 'rgba(148, 163, 184, 0.35)' },
  light: { color: '#334155', bgcolor: 'rgba(15, 23, 42, 0.06)', border: 'rgba(15, 23, 42, 0.12)' },
};

const getRoleChipSx = (roleName, isDark) => {
  const key = String(roleName || '').trim().toLowerCase();
  const palette = ROLE_CHIP_PALETTE[key] || ROLE_CHIP_FALLBACK;
  const tones = isDark ? palette.dark : palette.light;
  return {
    height: 22,
    textTransform: 'capitalize',
    fontWeight: 700,
    color: tones.color,
    bgcolor: tones.bgcolor,
    border: `1px solid ${tones.border}`,
  };
};

const PMS_BASE_PERMISSION_CODE = PERMISSIONS.BOOKING_OPERATIONS;
const INDEPENDENT_ADDITIONAL_ACCESS_CODES = new Set([
  PERMISSIONS.HOUSEKEEPING_MANAGE,
  PERMISSIONS.POS_RESTAURANT_MANAGE,
  PERMISSIONS.STOCK_MANAGE,
  PERMISSIONS.HALL_MANAGE,
]);
const isPmsDependentAdditionalAccess = (permissionCode) => (
  Boolean(permissionCode)
  && permissionCode !== PMS_BASE_PERMISSION_CODE
  && !INDEPENDENT_ADDITIONAL_ACCESS_CODES.has(permissionCode)
);
const AUDIT_EVENT_TYPES = [
  'role_created',
  'role_updated',
  'role_deleted',
  'user_roles_assigned',
  'user_roles_updated',
  'user_roles_removed',
];

const LandingUsers = ({ embedded = false, enableRoleManagement = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const themeColors = useMemo(() => getHousekeepingThemeColors(isDark), [isDark]);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface],
  );
  const djangoContext = window.DJANGO_CONTEXT || {};
  const userPermissionCodes = Array.isArray(djangoContext.user_permissions)
    ? djangoContext.user_permissions
    : [];
  const canManageRoles = enableRoleManagement
    && (userPermissionCodes.includes('*') || userPermissionCodes.includes('common.manage_roles'));

  // Add global style to prevent autofill background
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .MuiOutlinedInput-input:-webkit-autofill,
      .MuiOutlinedInput-input:-webkit-autofill:hover,
      .MuiOutlinedInput-input:-webkit-autofill:focus,
      .MuiOutlinedInput-input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 100px transparent inset !important;
        box-shadow: 0 0 0 100px transparent inset !important;
        background-color: transparent !important;
        -webkit-text-fill-color: ${isDark ? '#ddd' : '#212121'} !important;
        transition: background-color 5000s ease-in-out 0s !important;
      }
      [data-theme="dark"] .MuiOutlinedInput-input:-webkit-autofill,
      [data-theme="dark"] .MuiOutlinedInput-input:-webkit-autofill:hover,
      [data-theme="dark"] .MuiOutlinedInput-input:-webkit-autofill:focus,
      [data-theme="dark"] .MuiOutlinedInput-input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 100px #2a2a31 inset !important;
        box-shadow: 0 0 0 100px #2a2a31 inset !important;
        background-color: transparent !important;
        -webkit-text-fill-color: #ddd !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [isDark]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [serverTotalRecords, setServerTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: 'user',
    direction: 'asc',
  });
  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [currentUserOrg, setCurrentUserOrg] = useState(null); // Current user's organisation
  const [loadingHotels, setLoadingHotels] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
  const [rolesDialogView, setRolesDialogView] = useState('roles');
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesList, setRolesList] = useState([]);
  const [permissionsCatalog, setPermissionsCatalog] = useState([]);
  const [featurePermissionsCatalog, setFeaturePermissionsCatalog] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditEventTypeFilter, setAuditEventTypeFilter] = useState('');
  const [auditTargetUserIdFilter, setAuditTargetUserIdFilter] = useState('');
  const [auditDetailDrawerOpen, setAuditDetailDrawerOpen] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);
  const [auditPagination, setAuditPagination] = useState({
    page: 1,
    per_page: DEFAULT_AUDIT_PER_PAGE,
    total_pages: 1,
    total_records: 0,
  });
  const [roleForm, setRoleForm] = useState({
    id: null,
    name: '',
    permission_codes: [],
  });
  const [roleFormErrors, setRoleFormErrors] = useState({});
  const [savingRole, setSavingRole] = useState(false);
  const [deletingRoleId, setDeletingRoleId] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    is_active: true,
    login_email_otp_enabled: true,
    organisation_id: '',
    use_custom_access: false,
    hotel_ids: [],
    group_ids: [],
    feature_permission_codes: [],
  });
  const [formErrors, setFormErrors] = useState({});

  const currentUserId = Number(djangoContext.user?.id || djangoContext.user_id || 0);
  const selectedUserId = Number(selectedUser?.id || 0);
  const selectedUserIsOwner = Array.isArray(selectedUser?.groups)
    && selectedUser.groups.some((group) => String(group?.name || '').toLowerCase() === 'owner');
  const isEditingOwnOwnerRole = drawerMode === 'edit'
    && currentUserId > 0
    && selectedUserId === currentUserId
    && selectedUserIsOwner;

  const isOwnerRoleName = useCallback(
    (roleName) => String(roleName || '').trim().toLowerCase() === 'owner',
    [],
  );
  const isHiddenRoleName = useCallback(
    (roleName) => HIDDEN_ROLE_NAMES.has(String(roleName || '').trim().toLowerCase()),
    [],
  );

  const isOwnerUser = useCallback(
    (user) => Array.isArray(user?.groups)
      && user.groups.some((group) => isOwnerRoleName(group?.name)),
    [isOwnerRoleName],
  );

  // Get CSRF token from cookie
  const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(c => c.trim().startsWith('csrftoken='));
    return csrfCookie ? csrfCookie.split('=')[1] : '';
  };

  // Fetch users
  const fetchUsers = useCallback(async () => {
      try {
        setLoading(true);
      setError(null);
      const csrftoken = getCsrfToken();
      const headers = {
        'Accept': 'application/json',
        ...(csrftoken && { 'X-CSRFToken': csrftoken }),
      };

      const params = {
        page: 1,
        per_page: USERS_FETCH_LIMIT,
      };

      const response = await axios.get('/onboarding/api/users/', {
        params,
        withCredentials: true,
        headers,
      });

      if (response.data) {
        setUsers(response.data.users || []);
        if (response.data.pagination) {
          setServerTotalRecords(response.data.pagination.total_records || 0);
        }
      }
      } catch (err) {if (err.response?.status === 403) {
          setError('Access denied. You do not have permission to manage users.');
        } else {
          setError(err.response?.data?.error || 'Failed to load users. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
  }, []);

  // Fetch hotels
  const fetchHotels = useCallback(async () => {
    try {
      setLoadingHotels(true);
      const response = await axios.get('/onboarding/landing/?json=1', {
        withCredentials: true,
      });
      if (response.data && response.data.hotels) {
        setHotels(response.data.hotels);
      }
    } catch (err) {} finally {
      setLoadingHotels(false);
    }
  }, []);

  // Fetch current user's organisation
  const fetchCurrentUserOrg = useCallback(async () => {
    try {
      const profileData = await getUserProfile();
      if (profileData && profileData.organisation) {
        setCurrentUserOrg(profileData.organisation);
        // Set organisations to only include current user's organisation
        setOrganisations([profileData.organisation]);
      }
    } catch (err) {}
  }, []);

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get('/onboarding/api/groups/', {
        withCredentials: true,
      });
      if (response.data && response.data.groups) {
        setGroups(response.data.groups.filter((group) => !isHiddenRoleName(group?.name)));
      }
    } catch (err) {}
  }, [isHiddenRoleName]);

  const fetchFeaturePermissions = useCallback(async (hotelIds = []) => {
    try {
      const selectedHotelIds = Array.isArray(hotelIds) ? hotelIds.filter(Boolean) : [];
      const response = await axios.get('/onboarding/api/rbac/permissions/', {
        params: {
          feature_only: 1,
          ...(selectedHotelIds.length ? { hotel_ids: selectedHotelIds.join(',') } : {}),
        },
        withCredentials: true,
      });
      setFeaturePermissionsCatalog(response.data?.permissions || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load feature permissions.');
    }
  }, []);

  const groupedPermissions = useMemo(() => {
    return permissionsCatalog.filter((permission) =>
      isPermissionRelevantForHotel(permission.code)
    ).reduce((acc, permission) => {
      const appLabel = getPermissionGroupName(permission);
      if (!acc[appLabel]) {
        acc[appLabel] = [];
      }
      acc[appLabel].push(permission);
      return acc;
    }, {});
  }, [permissionsCatalog]);

  const selectedRolePermissionCodes = useMemo(() => {
    const selectedGroupIds = new Set((formData.group_ids || []).map((id) => String(id)));
    return groups
      .filter((group) => selectedGroupIds.has(String(group.id)))
      .flatMap((group) => (Array.isArray(group.permission_codes) ? group.permission_codes : []))
      .map((code) => String(code || '').trim())
      .filter(Boolean);
  }, [formData.group_ids, groups]);

  const selectedRolePermissionSet = useMemo(
    () => new Set(selectedRolePermissionCodes),
    [selectedRolePermissionCodes],
  );
  const selectedAdditionalAccessSet = useMemo(
    () => new Set(formData.feature_permission_codes || []),
    [formData.feature_permission_codes],
  );
  const hasPmsBaseAccess = selectedRolePermissionSet.has(PMS_BASE_PERMISSION_CODE)
    || selectedAdditionalAccessSet.has(PMS_BASE_PERMISSION_CODE);

  const additionalFeaturePermissionsCatalog = useMemo(
    () => featurePermissionsCatalog.filter((permission) => !selectedRolePermissionSet.has(permission.code)),
    [featurePermissionsCatalog, selectedRolePermissionSet],
  );

  const groupedFeaturePermissions = useMemo(() => {
    return additionalFeaturePermissionsCatalog.reduce((acc, permission) => {
      const appLabel = getPermissionGroupName(permission);
      if (!acc[appLabel]) {
        acc[appLabel] = [];
      }
      acc[appLabel].push(permission);
      return acc;
    }, {});
  }, [additionalFeaturePermissionsCatalog]);

  useEffect(() => {
    if (!openDrawer || drawerMode === 'view') return;
    if (!formData.hotel_ids || formData.hotel_ids.length === 0) {
      setFeaturePermissionsCatalog([]);
      return;
    }
    fetchFeaturePermissions(formData.hotel_ids || []);
  }, [openDrawer, drawerMode, formData.hotel_ids, fetchFeaturePermissions]);

  useEffect(() => {
    if (!openDrawer || drawerMode === 'view') return;
    if (additionalFeaturePermissionsCatalog.length === 0 && featurePermissionsCatalog.length === 0) return;
    const availableCodes = new Set(additionalFeaturePermissionsCatalog.map((permission) => permission.code));
    setFormData((prev) => {
      const currentCodes = prev.feature_permission_codes || [];
      const nextCodes = currentCodes.filter((code) => availableCodes.has(code));
      if (nextCodes.length === currentCodes.length) return prev;
      return { ...prev, feature_permission_codes: nextCodes };
    });
  }, [openDrawer, drawerMode, additionalFeaturePermissionsCatalog, featurePermissionsCatalog.length]);

  useEffect(() => {
    if (!openDrawer || drawerMode === 'view') return;
    if (hasPmsBaseAccess) return;
    setFormData((prev) => {
      const currentCodes = prev.feature_permission_codes || [];
      const nextCodes = currentCodes.filter((code) => !isPmsDependentAdditionalAccess(code));
      if (nextCodes.length === currentCodes.length) return prev;
      return { ...prev, feature_permission_codes: nextCodes };
    });
  }, [openDrawer, drawerMode, hasPmsBaseAccess]);

  const resetRoleForm = useCallback(() => {
    setRoleForm({
      id: null,
      name: '',
      permission_codes: [],
    });
    setRoleFormErrors({});
  }, []);

  const fetchRoleManagementData = useCallback(async () => {
    if (!canManageRoles) return;

    try {
      setRolesLoading(true);
      const [rolesResponse, permissionsResponse] = await Promise.all([
        axios.get('/onboarding/api/rbac/roles/', { withCredentials: true }),
        axios.get('/onboarding/api/rbac/permissions/', { withCredentials: true }),
      ]);

      setRolesList((rolesResponse.data?.roles || []).filter((role) => !isHiddenRoleName(role?.name)));
      setPermissionsCatalog(permissionsResponse.data?.permissions || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load role management data.');
    } finally {
      setRolesLoading(false);
    }
  }, [canManageRoles, isHiddenRoleName]);

  const handleOpenRolesDialog = useCallback(async () => {
    if (!canManageRoles) return;
    setRolesDialogOpen(true);
    setRolesDialogView('roles');
    resetRoleForm();
    await fetchRoleManagementData();
  }, [canManageRoles, fetchRoleManagementData, resetRoleForm]);

  const handleCloseRolesDialog = useCallback(() => {
    if (savingRole) return;
    setRolesDialogOpen(false);
    setRolesDialogView('roles');
    setAuditDetailDrawerOpen(false);
    setSelectedAuditLog(null);
    setDeletingRoleId(null);
    resetRoleForm();
  }, [resetRoleForm, savingRole]);

  const handleRoleSelect = useCallback((role) => {
    setRoleForm({
      id: role.id,
      name: role.name || '',
      permission_codes: Array.isArray(role.permission_codes) ? role.permission_codes : [],
    });
    setRoleFormErrors({});
  }, []);

  const handleRoleSave = useCallback(async () => {
    const errors = {};
    if (!roleForm.name || !roleForm.name.trim()) {
      errors.name = 'Role name is required.';
    }
    if (Object.keys(errors).length > 0) {
      setRoleFormErrors(errors);
      return;
    }

    try {
      setSavingRole(true);
      const payload = {
        name: roleForm.name.trim(),
        permission_codes: roleForm.permission_codes || [],
      };

      if (roleForm.id) {
        await axios.put(`/onboarding/api/rbac/roles/${roleForm.id}/`, payload, {
          withCredentials: true,
        });
        setSuccessMessage('Role updated successfully.');
      } else {
        await axios.post('/onboarding/api/rbac/roles/', payload, {
          withCredentials: true,
        });
        setSuccessMessage('Role created successfully.');
      }
      setSuccess(true);
      await fetchRoleManagementData();
      await fetchGroups();
      resetRoleForm();
    } catch (err) {
      if (err.response?.data?.errors) {
        setRoleFormErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.error || 'Failed to save role.');
      }
    } finally {
      setSavingRole(false);
    }
  }, [fetchGroups, fetchRoleManagementData, resetRoleForm, roleForm]);

  const handleRoleDelete = useCallback(async () => {
    if (!roleForm.id) return;
    if (isOwnerRoleName(roleForm.name)) {
      setError(OWNER_ROLE_SUPPORT_MESSAGE);
      return;
    }
    try {
      setDeletingRoleId(roleForm.id);
      await axios.delete(`/onboarding/api/rbac/roles/${roleForm.id}/`, {
        withCredentials: true,
      });
      setSuccessMessage('Role deleted successfully.');
      setSuccess(true);
      await fetchRoleManagementData();
      await fetchGroups();
      resetRoleForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete role.');
    } finally {
      setDeletingRoleId(null);
    }
  }, [fetchGroups, fetchRoleManagementData, isOwnerRoleName, resetRoleForm, roleForm.id, roleForm.name]);

  const fetchRbacAuditLogs = useCallback(
    async ({
      pageValue = 1,
      perPage = auditPagination.per_page,
      eventType = auditEventTypeFilter,
      targetUserId = auditTargetUserIdFilter,
    } = {}) => {
      if (!canManageRoles) return;

      try {
        setAuditLogsLoading(true);
        const params = {
          page: pageValue,
          per_page: perPage,
        };
        if (eventType) {
          params.event_type = eventType;
        }
        if (targetUserId) {
          params.target_user_id = targetUserId;
        }

        const response = await axios.get('/onboarding/api/rbac/audit-logs/', {
          params,
          withCredentials: true,
        });

        const logs = response.data?.logs || [];
        const pagination = response.data?.pagination || {};
        setAuditLogs(logs);
        setAuditPagination({
          page: pagination.page || pageValue,
          per_page: pagination.per_page || perPage,
          total_pages: pagination.total_pages || 1,
          total_records: pagination.total_records || logs.length,
        });
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load RBAC audit logs.');
      } finally {
        setAuditLogsLoading(false);
      }
    },
    [auditEventTypeFilter, auditPagination.per_page, auditTargetUserIdFilter, canManageRoles]
  );

  const handleRolesDialogViewChange = useCallback(
    (view) => {
      setRolesDialogView(view);
      if (view !== 'audit') {
        setAuditDetailDrawerOpen(false);
        setSelectedAuditLog(null);
      }
      if (view === 'audit') {
        fetchRbacAuditLogs({ pageValue: 1 });
      }
    },
    [fetchRbacAuditLogs]
  );

  const handleAuditApplyFilters = useCallback(() => {
    fetchRbacAuditLogs({
      pageValue: 1,
      eventType: auditEventTypeFilter,
      targetUserId: auditTargetUserIdFilter,
    });
  }, [auditEventTypeFilter, auditTargetUserIdFilter, fetchRbacAuditLogs]);

  const handleAuditResetFilters = useCallback(() => {
    setAuditEventTypeFilter('');
    setAuditTargetUserIdFilter('');
    fetchRbacAuditLogs({
      pageValue: 1,
      eventType: '',
      targetUserId: '',
    });
  }, [fetchRbacAuditLogs]);

  const handleAuditPageChange = useCallback(
    (event, newPage) => {
      fetchRbacAuditLogs({ pageValue: newPage + 1 });
    },
    [fetchRbacAuditLogs]
  );

  const handleAuditRowsPerPageChange = useCallback(
    (event) => {
      const perPage = parseInt(event.target.value, 10);
      fetchRbacAuditLogs({ pageValue: 1, perPage });
    },
    [fetchRbacAuditLogs]
  );

  const handleOpenAuditDetail = useCallback((log) => {
    setSelectedAuditLog(log || null);
    setAuditDetailDrawerOpen(true);
  }, []);

  const handleCloseAuditDetail = useCallback(() => {
    setAuditDetailDrawerOpen(false);
    setSelectedAuditLog(null);
  }, []);

  const formatAuditTimestamp = useCallback((value) => {
    if (!value) return '-';
    return moment(value).format('YYYY-MM-DD HH:mm:ss');
  }, []);

  const stringifyAuditPayload = useCallback((value) => {
    if (!value || typeof value !== 'object') {
      return 'No data';
    }
    if (Array.isArray(value) && value.length === 0) {
      return 'No data';
    }
    if (!Array.isArray(value) && Object.keys(value).length === 0) {
      return 'No data';
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return 'Unable to render payload.';
    }
  }, []);

  const copyTextToClipboard = useCallback(async (text) => {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (!copied) {
      throw new Error('Copy failed');
    }
  }, []);

  const handleCopyAuditPayload = useCallback(async (label, payload) => {
    try {
      const formattedPayload = stringifyAuditPayload(payload);
      await copyTextToClipboard(formattedPayload === 'No data' ? '{}' : formattedPayload);
      setSuccessMessage(`${label} copied.`);
      setSuccess(true);
    } catch (error) {
      setError(`Unable to copy ${label.toLowerCase()}.`);
    }
  }, [copyTextToClipboard, stringifyAuditPayload]);

  useEffect(() => {
    fetchUsers();
    fetchCurrentUserOrg(); // Fetch current user's organisation on mount
  }, [fetchUsers, fetchCurrentUserOrg]);

  useEffect(() => {
    if (openDrawer && (drawerMode === 'add' || drawerMode === 'edit')) {
      fetchHotels();
      fetchCurrentUserOrg();
      fetchGroups();
    }
  }, [openDrawer, drawerMode, fetchHotels, fetchCurrentUserOrg, fetchGroups]);

  // Auto-fill organisation_id when currentUserOrg is available and drawer is open
  useEffect(() => {
    if (openDrawer && currentUserOrg?.id) {
      setFormData((prev) => ({
        ...prev,
        organisation_id: currentUserOrg.id,
      }));
    }
  }, [openDrawer, currentUserOrg]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page on search
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Map the form field name back to the correct state key
    const stateKey = name === 'user_username' ? 'username' : name;
    setFormData((prev) => ({
      ...prev,
      [stateKey]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user changes input (use stateKey for errors too)
    if (formErrors[stateKey]) {
      setFormErrors((prev) => ({
        ...prev,
        [stateKey]: '',
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (drawerMode === 'add' && !formData.username.trim()) {
      errors.username = 'Username is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (drawerMode === 'add' && !formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    // Confirm password validation (only required when adding new user)
    if (drawerMode === 'add') {
      if (!formData.confirm_password || formData.confirm_password.trim() === '') {
        errors.confirm_password = 'Please confirm your password';
      } else if (formData.password !== formData.confirm_password) {
        errors.confirm_password = 'Passwords do not match';
      }
    } else if (formData.password) {
      if (!formData.confirm_password || formData.confirm_password.trim() === '') {
        errors.confirm_password = 'Please confirm your new password';
      } else if (formData.password !== formData.confirm_password) {
        errors.confirm_password = 'Passwords do not match';
      }
    }

    // Organisation is automatically set to current user's organisation
    if (!currentUserOrg?.id) {
      errors.organisation_id = 'Organisation is required';
    } else {
      // Auto-set to current user's organisation
      formData.organisation_id = currentUserOrg.id;
    }

    if (!formData.hotel_ids || formData.hotel_ids.length === 0) {
      errors.hotel_ids = 'At least one hotel is required';
    } else {
      const allowedHotelIds = new Set(hotels.map((hotel) => Number(hotel.id)));
      const hasOutOfScopeHotel = formData.hotel_ids.some(
        (hotelId) => !allowedHotelIds.has(Number(hotelId))
      );
      if (hasOutOfScopeHotel) {
        errors.hotel_ids = 'You can only assign users to hotels you manage';
      }
    }

    if (formData.use_custom_access) {
      if (!formData.feature_permission_codes || formData.feature_permission_codes.length === 0) {
        errors.feature_permission_codes = 'Select at least one additional access permission for Custom Access.';
      }
    } else if (!formData.group_ids || formData.group_ids.length !== 1) {
      errors.group_ids = 'Select exactly one role';
    }

    const selectedAdditionalCodes = formData.feature_permission_codes || [];
    const hasInvalidPmsDependentAccess = selectedAdditionalCodes.some((code) => (
      isPmsDependentAdditionalAccess(code)
      && !(
        selectedRolePermissionSet.has(PMS_BASE_PERMISSION_CODE)
        || selectedAdditionalCodes.includes(PMS_BASE_PERMISSION_CODE)
      )
    ));
    if (hasInvalidPmsDependentAccess) {
      errors.feature_permission_codes = 'PMS-dependent access requires Booking Operations access from the role or Additional Access.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = () => {
    setDrawerMode('add');
    setSelectedUser(null);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      confirm_password: '',
      is_active: true,
      login_email_otp_enabled: true,
      organisation_id: currentUserOrg?.id || '',
      use_custom_access: false,
      hotel_ids: [],
      group_ids: [],
      feature_permission_codes: [],
    });
    setFormErrors({});
    setOpenDrawer(true);
  };

  const handleEdit = (user) => {
    setDrawerMode('edit');
    setSelectedUser(user);
    const hotelIds = user.hotels ? user.hotels.map(h => h.id) : [];
    const groupIds = user.groups ? user.groups.map(g => g.id).slice(0, 1) : [];
    const directFeaturePermissionCodes = Array.isArray(user.direct_feature_permission_codes)
      ? user.direct_feature_permission_codes
      : [];
    setFormData({
      username: user.username,
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      password: '',
      confirm_password: '',
      is_active: user.is_active,
      login_email_otp_enabled: user.login_email_otp_enabled !== false,
      organisation_id: currentUserOrg?.id || user.organisation?.id || '', // Always use current user's org first
      use_custom_access: groupIds.length === 0,
      hotel_ids: hotelIds,
      group_ids: groupIds,
      feature_permission_codes: directFeaturePermissionCodes,
    });
    setFormErrors({});
    setOpenDrawer(true);
  };

  const handleView = (user) => {
    setDrawerMode('view');
    setSelectedUser(user);
    setOpenDrawer(true);
  };

  const handleDelete = (user) => {
    if (isOwnerUser(user)) {
      setError(OWNER_ROLE_SUPPORT_MESSAGE);
      return;
    }
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    try {
      const csrftoken = getCsrfToken();
      const headers = {
        'Accept': 'application/json',
        ...(csrftoken && { 'X-CSRFToken': csrftoken }),
      };

      await axios.delete(`/onboarding/api/users/${userToDelete.id}/`, {
        withCredentials: true,
        headers,
      });

      setSuccessMessage('User deleted successfully');
      setSuccess(true);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {setError(err.response?.data?.error || 'Failed to delete user');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const csrftoken = getCsrfToken();
      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(csrftoken && { 'X-CSRFToken': csrftoken }),
      };

      const       data = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        is_active: formData.is_active,
        login_email_otp_enabled: formData.login_email_otp_enabled,
        use_custom_access: Boolean(formData.use_custom_access),
      };

      if (drawerMode === 'add') {
        data.username = formData.username;
        data.password = formData.password;
      } else if (formData.password) {
        data.password = formData.password;
      }

      // Organisation is required
      if (formData.organisation_id) {
        data.organisation_id = parseInt(formData.organisation_id);
      } else {
        // This should not happen if validation is working, but ensure it's sent
        throw new Error('Organisation is required');
      }

      if (formData.hotel_ids && formData.hotel_ids.length > 0) {
        data.hotel_ids = formData.hotel_ids.map(id => parseInt(id));
      }

      data.group_ids = Array.isArray(formData.group_ids)
        ? formData.group_ids.map(id => parseInt(id))
        : [];
      if (formData.use_custom_access) {
        data.group_ids = [];
      }
      data.feature_permission_codes = Array.isArray(formData.feature_permission_codes)
        ? formData.feature_permission_codes.filter((code) => !selectedRolePermissionSet.has(code))
        : [];

      let response;
      if (drawerMode === 'add') {
        response = await axios.post('/onboarding/api/users/', data, {
          withCredentials: true,
          headers,
        });
      } else {
        response = await axios.put(`/onboarding/api/users/${selectedUser.id}/`, data, {
          withCredentials: true,
          headers,
        });
      }

      setSuccessMessage(
        drawerMode === 'add' ? 'User created successfully' : 'User updated successfully'
      );
      setSuccess(true);
      setOpenDrawer(false);
      fetchUsers();
    } catch (err) {if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.error || 'Failed to save user');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setSelectedUser(null);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      confirm_password: '',
      is_active: true,
      login_email_otp_enabled: true,
      organisation_id: '',
      use_custom_access: false,
      hotel_ids: [],
      group_ids: [],
      feature_permission_codes: [],
    });
    setFormErrors({});
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Never';
    return formatHotelDateTime(dateString, getHotelTimezone()) || 'Never';
  }, []);

  /** Human-readable last login in hotel config timezone only, e.g. 08 Aug 2026, 8:40 AM */
  const formatLastLogin = useCallback((dateString) => {
    if (!dateString) return 'Never';
    const tz = getHotelTimezone();
    const m = moment.utc(dateString);
    if (!m.isValid()) return 'Never';
    const zoned = moment.tz ? m.tz(tz) : m.utc();
    return zoned.format('DD MMM YYYY, h:mm A');
  }, []);

  const getInitials = (user) => {
    if (user.first_name || user.last_name) {
      return `${(user.first_name || '').charAt(0)}${(user.last_name || '').charAt(0)}`.toUpperCase();
    }
    return user.username?.charAt(0).toUpperCase() || 'U';
  };

  const getRoleText = useCallback((user) => {
    if (user.groups && user.groups.length > 0) {
      return user.groups.map((group) => group.name).join(', ');
    }
    return 'Custom Access';
  }, []);

  const getHotelText = useCallback((user) => {
    if (user.hotels && user.hotels.length > 0) {
      return user.hotels.map((hotel) => hotel.name).join(', ');
    }
    return '-';
  }, []);

  const getSearchableLastLogin = useCallback((user) => {
    if (!user.last_login) return '';
    return formatLastLogin(user.last_login).toLowerCase();
  }, [formatLastLogin]);

  const getUserText = useCallback((user) => `${user.full_name || ''} ${user.username || ''}`.trim(), []);

  const getLastLoginText = useCallback((user) => formatLastLogin(user.last_login), [formatLastLogin]);

  const columnFilterAccessors = useMemo(() => ({
    user: getUserText,
    email: (user) => user.email || '-',
    role: getRoleText,
    status: (user) => user.status || '-',
    hotel: getHotelText,
    lastLogin: getLastLoginText,
  }), [getUserText, getRoleText, getHotelText, getLastLoginText]);

  const columnFilterOptions = useMemo(() => (
    Object.keys(columnFilterAccessors).reduce((acc, key) => {
      const uniqueValues = [...new Set(
        users
          .map((user) => String(columnFilterAccessors[key](user) || '').trim())
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      acc[key] = uniqueValues;
      return acc;
    }, {})
  ), [users, columnFilterAccessors]);

  const handleColumnFilterChange = useCallback((field, value) => {
    setPage(0);
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const clearColumnFilters = useCallback(() => {
    setPage(0);
    setColumnFilters({});
  }, []);

  const filteredUsers = useMemo(() => {
    const globalQuery = searchQuery.trim().toLowerCase();

    const filtered = users.filter((user) => {
      const userText = getUserText(user).toLowerCase();
      const emailText = (user.email || '').toLowerCase();
      const roleText = getRoleText(user).toLowerCase();
      const statusText = (user.status || '').toLowerCase();
      const hotelText = getHotelText(user).toLowerCase();
      const lastLoginText = getSearchableLastLogin(user);

      const matchesGlobal = !globalQuery
        || [userText, emailText, roleText, statusText, hotelText, lastLoginText]
          .some((value) => value.includes(globalQuery));

      const matchesColumnFilters = Object.entries(columnFilters).every(([field, selectedValue]) => {
        if (!selectedValue) return true;
        const accessor = columnFilterAccessors[field];
        if (!accessor) return true;
        const value = String(accessor(user) || '').trim();
        if (field === 'user' || field === 'email' || field === 'lastLogin') {
          return value.toLowerCase().includes(String(selectedValue).toLowerCase());
        }
        return value === selectedValue;
      });

      return matchesGlobal && matchesColumnFilters;
    });

    const sorted = [...filtered].sort((a, b) => {
      const directionMultiplier = sortConfig.direction === 'asc' ? 1 : -1;

      const aValueMap = {
        user: `${a.full_name || ''} ${a.username || ''}`.trim().toLowerCase(),
        email: (a.email || '').toLowerCase(),
        role: getRoleText(a).toLowerCase(),
        status: (a.status || '').toLowerCase(),
        hotel: getHotelText(a).toLowerCase(),
        lastLogin: a.last_login ? new Date(a.last_login).getTime() : 0,
      };

      const bValueMap = {
        user: `${b.full_name || ''} ${b.username || ''}`.trim().toLowerCase(),
        email: (b.email || '').toLowerCase(),
        role: getRoleText(b).toLowerCase(),
        status: (b.status || '').toLowerCase(),
        hotel: getHotelText(b).toLowerCase(),
        lastLogin: b.last_login ? new Date(b.last_login).getTime() : 0,
      };

      const aValue = aValueMap[sortConfig.key];
      const bValue = bValueMap[sortConfig.key];

      if (aValue < bValue) return -1 * directionMultiplier;
      if (aValue > bValue) return 1 * directionMultiplier;
      return 0;
    });

    return sorted;
  }, [
    users,
    searchQuery,
    columnFilters,
    columnFilterAccessors,
    getUserText,
    sortConfig,
    getRoleText,
    getHotelText,
    getSearchableLastLogin,
  ]);

  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredUsers.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredUsers.length, page, rowsPerPage]);

  const userTableColumns = useMemo(() => [
    {
      field: 'user',
      label: 'User',
      sortable: true,
      render: (value, user) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 180 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: THEME_COLOR,
              color: '#03241c',
              fontSize: '0.875rem',
              fontWeight: 800,
              boxShadow: `0 0 0 3px ${isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.18)'}`,
            }}
          >
            {getInitials(user)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#e5e7eb' : '#111827' }}>
              {user.full_name || user.username || 'User'}
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
              @{user.username || 'unknown'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'email',
      label: 'Email',
      sortable: true,
      render: (value, user) => (
        <Typography variant="body2" sx={{ color: isDark ? '#d1d5db' : '#334155', minWidth: 180 }}>
          {user.email || '-'}
        </Typography>
      ),
    },
    {
      field: 'role',
      label: 'Role',
      sortable: true,
      render: (value, user) => (
        user.groups && user.groups.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 120 }}>
            {user.groups.map((group) => (
              <Chip
                key={group.id}
                label={group.name}
                size="small"
                sx={getRoleChipSx(group.name, isDark)}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
            No groups
          </Typography>
        )
      ),
    },
    {
      field: 'status',
      label: 'Status',
      sortable: true,
      render: (value, user) => (
        <Chip
          label={user.status}
          size="small"
          color={getStatusColor(user.status)}
          sx={{ textTransform: 'capitalize', fontWeight: 800, height: 24 }}
        />
      ),
    },
    {
      field: 'hotel',
      label: 'Hotel',
      sortable: true,
      render: (value, user) => (
        <Typography variant="body2" sx={{ color: isDark ? '#d1d5db' : '#334155', minWidth: 170 }}>
          {getHotelText(user)}
        </Typography>
      ),
    },
    {
      field: 'lastLogin',
      label: 'Last Login',
      sortable: true,
      render: (value, user) => (
        <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', minWidth: 160 }}>
          {formatLastLogin(user.last_login)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      render: (value, user) => {
        const ownerUser = isOwnerUser(user);
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() => handleView(user)}
                sx={{
                  color: THEME_COLOR,
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.08)',
                  },
                }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleEdit(user)}
                sx={{
                  color: isDark ? '#4fc3f7' : '#1976d2',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={ownerUser ? OWNER_ROLE_SUPPORT_MESSAGE : 'Delete'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(user)}
                  disabled={ownerUser}
                  sx={{
                    color: ownerUser
                      ? (isDark ? 'rgba(148,163,184,0.38)' : 'rgba(100,116,139,0.45)')
                      : (isDark ? '#ef5350' : '#d32f2f'),
                    '&:hover': {
                      backgroundColor: ownerUser
                        ? 'transparent'
                        : (isDark ? 'rgba(239, 83, 80, 0.1)' : 'rgba(211, 47, 47, 0.08)'),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [formatLastLogin, getHotelText, isDark, isOwnerUser]);

  const filterSelectSx = useMemo(() => ({
    minWidth: 126,
    '& .MuiOutlinedInput-root': {
      height: 36,
      borderRadius: '10px',
      background: isDark ? 'rgba(15, 23, 42, 0.52)' : 'rgba(248, 250, 252, 0.92)',
      '& fieldset': {
        borderColor: isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.12)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(76, 225, 182, 0.55)',
      },
      '&.Mui-focused fieldset': {
        borderColor: THEME_COLOR,
      },
    },
    '& .MuiSelect-select': {
      py: 0.6,
      fontSize: 12,
      color: isDark ? '#e5e7eb' : '#0f172a',
    },
  }), [isDark]);

  const userTableFilterRow = useMemo(() => (
    userTableColumns.map((column) => {
      if (column.field === 'actions') {
        return (
          <Button
            key="clear-filters"
            size="small"
            onClick={clearColumnFilters}
            disabled={!Object.values(columnFilters).some(Boolean)}
            sx={{ color: THEME_COLOR, textTransform: 'none', fontWeight: 700 }}
          >
            Clear
          </Button>
        );
      }

      if (column.field === 'user' || column.field === 'email' || column.field === 'lastLogin') {
        return (
          <TextField
            key={column.field}
            size="small"
            value={columnFilters[column.field] || ''}
            onChange={(event) => handleColumnFilterChange(column.field, event.target.value)}
            placeholder={`Search ${column.label.toLowerCase()}...`}
            autoComplete="off"
            sx={{
              ...filterSelectSx,
              minWidth: column.field === 'email' ? 170 : column.field === 'lastLogin' ? 170 : 150,
              '& .MuiOutlinedInput-input': {
                py: 0.65,
                fontSize: 12,
                color: isDark ? '#e5e7eb' : '#0f172a',
              },
            }}
          />
        );
      }

      return (
        <FormControl key={column.field} size="small" fullWidth sx={filterSelectSx}>
          <Select
            value={columnFilters[column.field] || ''}
            onChange={(event) => handleColumnFilterChange(column.field, event.target.value)}
            displayEmpty
            renderValue={(selected) => selected || `All ${column.label}`}
          >
            <MenuItem value="">All {column.label}</MenuItem>
            {(columnFilterOptions[column.field] || []).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    })
  ), [
    userTableColumns,
    clearColumnFilters,
    columnFilters,
    columnFilterOptions,
    filterSelectSx,
    handleColumnFilterChange,
  ]);

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, sm: 3 } }}>
      {!embedded && (
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="Users"
            breadcrumbs={[
              { label: 'Home', path: '/onboarding/landing/' },
              { label: 'Users' },
            ]}
            navLinks={[
              { url: '/onboarding/landing/?view=dashboard', label: 'Dashboard' },
            ]}
          />
        </Box>
      )}

      <Box
        sx={{
          borderRadius: embedded ? '14px' : '12px',
          backgroundColor: embedded
            ? (isDark ? 'rgba(15, 23, 42, 0.28)' : '#ffffff')
            : (isDark ? '#2a2a31' : '#ffffff'),
          border: embedded
            ? `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.1)'}`
            : `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
          boxShadow: embedded ? 'none' : undefined,
          p: embedded ? { xs: 2, sm: 2.3 } : 0,
        }}
      >
        {embedded && (
          <Box
            sx={{
              mb: 2,
              p: 1.4,
              borderRadius: '12px',
              background: isDark
                ? 'linear-gradient(135deg, rgba(76,225,182,0.14), rgba(76,225,182,0.04))'
                : 'linear-gradient(135deg, rgba(76,225,182,0.12), rgba(76,225,182,0.05))',
              border: `1px solid ${isDark ? 'rgba(76,225,182,0.26)' : 'rgba(76,225,182,0.2)'}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: '#4ce1b6', fontSize: 13 }}>
              User Access Settings
            </Typography>
            <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12 }}>
              Manage users, role groups, and direct feature access from one place.
            </Typography>
          </Box>
        )}

        <Box sx={{ p: embedded ? 0 : 3 }}>
          {/* Header with Search and Add Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? '#fff' : '#212121',
              }}
            >
              User Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                placeholder="Search all columns..."
                size="small"
                value={searchQuery}
                onChange={handleSearchChange}
                autoComplete="off"
                name="search-users-field"
                id="search-users-field-input"
                type="search"
                inputProps={{
                  autoComplete: 'off',
                  'data-lpignore': 'true',
                  'data-form-type': 'other',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: '100%', sm: '250px' },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'transparent !important',
                    '& .MuiOutlinedInput-input': {
                      backgroundColor: 'transparent !important',
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                        WebkitTextFillColor: isDark ? '#ddd' : '#212121',
                        caretColor: isDark ? '#ddd' : '#212121',
                        transition: 'background-color 5000s ease-in-out 0s',
                        backgroundColor: 'transparent !important',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                        backgroundColor: 'transparent !important',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                        backgroundColor: 'transparent !important',
                      },
                      '&:-webkit-autofill:active': {
                        WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                        backgroundColor: 'transparent !important',
                      },
                    },
                    '& fieldset': {
                      borderColor: isDark ? '#333246' : '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: isDark ? '#555' : '#999',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: THEME_COLOR,
                    },
                  },
                }}
              />
            {canManageRoles && (
              <Button
                variant="outlined"
                startIcon={<ManageAccountsIcon />}
                onClick={handleOpenRolesDialog}
                sx={{
                  borderColor: THEME_COLOR,
                  color: THEME_COLOR,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: THEME_COLOR,
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                  },
                }}
              >
                Manage Roles
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                bgcolor: THEME_COLOR,
                color: '#000',
                fontWeight: 600,
                textTransform: 'none',
                '& .MuiButton-startIcon': { color: '#000' },
                '&:hover': {
                  bgcolor: THEME_COLOR,
                  opacity: 0.9,
                  color: '#000',
                  '& .MuiButton-startIcon': { color: '#000' },
                },
              }}
            >
              Add User
            </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              p: { xs: 1.2, sm: 1.5 },
              borderRadius: '18px',
              background: isDark
                ? 'linear-gradient(135deg, rgba(15,23,42,0.75), rgba(30,41,59,0.36))'
                : 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.92))',
              border: `1px solid ${isDark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.08)'}`,
              boxShadow: isDark
                ? '0 18px 42px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.04)'
                : '0 18px 42px rgba(15,23,42,0.08)',
              '& .data-table-wrapper': {
                borderRadius: '14px',
              },
            }}
          >
            <DataTable
              columns={userTableColumns}
              data={paginatedUsers}
              loading={loading}
              pagination={{
                page,
                perPage: rowsPerPage,
                totalRecords: filteredUsers.length,
              }}
              onPageChange={(newPage) => setPage(newPage)}
              onPerPageChange={(newPerPage) => {
                setRowsPerPage(newPerPage);
                setPage(0);
              }}
              onSortChange={({ column, direction }) => {
                setSortConfig({ key: column, direction });
                setPage(0);
              }}
              sortConfig={{ column: sortConfig.key, direction: sortConfig.direction }}
              filterRow={userTableFilterRow}
              emptyMessage={
                searchQuery || Object.values(columnFilters).some(Boolean)
                  ? 'No users match the current search or column filters.'
                  : 'No users found'
              }
              skeletonRowCount={5}
              optimizeScroll
            />
          </Box>
          {!loading && serverTotalRecords > USERS_FETCH_LIMIT && (
            <Typography
              sx={{
                mt: 1,
                fontSize: 12,
                color: isDark ? '#94a3b8' : '#64748b',
              }}
            >
              Showing first {USERS_FETCH_LIMIT} users out of {serverTotalRecords}. Refine filters to narrow results.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Add/Edit/View Drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleCloseDrawer}
        disableAutoFocus
        disableEnforceFocus
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            ...getHousekeepingDrawerPaperSx(isDark),
            backgroundColor: isDark ? themeColors.surface : '#ffffff',
            backgroundImage: 'none',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            ...getHousekeepingDrawerHeaderSx(isDark),
            flexShrink: 0,
            backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
            backgroundImage: 'none',
          }}
        >
          <DrawerHeaderTitle
            icon={
              drawerMode === 'add'
                ? <PersonAddIcon sx={{ fontSize: 22 }} />
                : drawerMode === 'edit'
                  ? <EditIcon sx={{ fontSize: 22 }} />
                  : <ViewIcon sx={{ fontSize: 22 }} />
            }
            title={
              drawerMode === 'add'
                ? 'Add New User'
                : drawerMode === 'edit'
                  ? 'Edit User'
                  : 'User Details'
            }
            subtitle={currentUserOrg?.name ? `Organisation: ${currentUserOrg.name}` : undefined}
            isDark={isDark}
          />
          <IconButton
            onClick={handleCloseDrawer}
            size="small"
            sx={{
              color: isDark ? '#999' : '#666',
              flexShrink: 0,
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
            }}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            '& .MuiTextField-root, & .MuiFormControl-root': {
              ...getSoftFieldSx(isDark),
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px !important',
              backgroundColor: `${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)'} !important`,
              transition: 'background-color 0.2s ease',
              '& fieldset': {
                border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.14)'} !important`,
              },
              '&:hover fieldset': {
                borderColor: `${isDark ? 'rgba(148, 163, 184, 0.38)' : 'rgba(15, 23, 42, 0.24)'} !important`,
              },
              '&.Mui-focused fieldset': {
                borderColor: `${THEME_COLOR} !important`,
              },
              '&.Mui-error fieldset': {
                borderColor: '#f44336 !important',
              },
              '&:hover': {
                backgroundColor: `${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.055)'} !important`,
              },
              '&.Mui-focused': {
                backgroundColor: `${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.055)'} !important`,
                boxShadow: 'none',
              },
              '&.Mui-error': {
                backgroundColor: `${isDark ? 'rgba(244,67,54,0.12)' : 'rgba(244,67,54,0.08)'} !important`,
              },
            },
            '& .MuiInputLabel-root': {
              color: `${isDark ? '#8a939e' : '#78828c'} !important`,
              '&.Mui-focused': { color: `${isDark ? '#8a939e' : '#78828c'} !important` },
              '&.Mui-error': { color: '#f44336 !important' },
            },
          }}
        >
          {drawerMode === 'view' ? (
            // View Mode
            selectedUser && (() => {
              const roles = Array.isArray(selectedUser.groups) ? selectedUser.groups : [];
              const assignedHotels = Array.isArray(selectedUser.hotels) ? selectedUser.hotels : [];
              const detailCardSx = {
                p: 1.5,
                borderRadius: 3,
                border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.16)' : 'rgba(15, 23, 42, 0.08)'}`,
                backgroundColor: isDark ? 'rgba(255,255,255,0.035)' : '#ffffff',
                boxShadow: isDark ? 'none' : '0 10px 26px rgba(15, 23, 42, 0.05)',
                height: '100%',
              };
              const mutedText = isDark ? '#94a3b8' : '#64748b';
              const strongText = isDark ? '#f8fafc' : '#0f172a';

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      background: isDark
                        ? 'linear-gradient(135deg, rgba(76,225,182,0.16), rgba(59,130,246,0.08))'
                        : 'linear-gradient(135deg, rgba(76,225,182,0.2), rgba(236,253,245,0.9))',
                      border: `1px solid ${isDark ? 'rgba(76,225,182,0.24)' : 'rgba(76,225,182,0.36)'}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                      <Avatar
                        sx={{
                          width: 78,
                          height: 78,
                          bgcolor: THEME_COLOR,
                          color: '#064e3b',
                          fontSize: '2rem',
                          fontWeight: 900,
                          boxShadow: '0 14px 30px rgba(76,225,182,0.28)',
                        }}
                      >
                        {getInitials(selectedUser)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: strongText, lineHeight: 1.15 }} noWrap>
                          {selectedUser.full_name || selectedUser.username || 'User'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: mutedText, mt: 0.4 }} noWrap>
                          @{selectedUser.username || 'username'}
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                          <Chip
                            label={selectedUser.status || 'unknown'}
                            color={getStatusColor(selectedUser.status)}
                            size="small"
                            sx={{ textTransform: 'capitalize', fontWeight: 800 }}
                          />
                          <Chip
                            icon={<AdminPanelSettingsIcon sx={{ fontSize: 16 }} />}
                            label={`${roles.length || 0} role${roles.length === 1 ? '' : 's'}`}
                            size="small"
                            sx={{ fontWeight: 800, backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9' }}
                          />
                        </Stack>
                      </Box>
                    </Box>
                  </Paper>

                  <Grid container spacing={1.5}>
                    {[
                      { label: 'Email', value: selectedUser.email || '-', icon: <EmailIcon /> },
                      { label: 'Organisation', value: selectedUser.organisation?.name || currentUserOrg?.name || '-', icon: <BusinessIcon /> },
                      { label: 'System Role', value: selectedUser.role || '-', icon: <BadgeIcon /> },
                      { label: 'Login OTP', value: selectedUser.login_email_otp_enabled === false ? 'Disabled' : 'Enabled', icon: <SecurityIcon /> },
                      { label: 'Assigned Hotels', value: assignedHotels.length ? assignedHotels.map((hotel) => hotel.name).join(', ') : '-', icon: <HotelIcon /> },
                      { label: 'Date Joined', value: formatDate(selectedUser.date_joined), icon: <CalendarTodayIcon /> },
                      { label: 'Last Login', value: formatLastLogin(selectedUser.last_login), icon: <AccessTimeIcon /> },
                    ].map((item) => (
                      <Grid item xs={12} sm={6} key={item.label}>
                        <Box sx={detailCardSx}>
                          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 2,
                                display: 'grid',
                                placeItems: 'center',
                                color: THEME_COLOR,
                                backgroundColor: isDark ? 'rgba(76,225,182,0.1)' : 'rgba(76,225,182,0.16)',
                                flexShrink: 0,
                                '& svg': { fontSize: 18 },
                              }}
                            >
                              {item.icon}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ color: mutedText, fontSize: '0.68rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {item.label}
                              </Typography>
                              <Typography sx={{ color: strongText, fontWeight: 700, mt: 0.35, overflowWrap: 'anywhere' }}>
                                {item.value}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Paper elevation={0} sx={detailCardSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
                      <Box>
                        <Typography sx={{ color: strongText, fontWeight: 900 }}>Assigned Roles</Typography>
                        <Typography sx={{ color: mutedText, fontSize: '0.78rem' }}>
                          Role permissions provide the default access for this user.
                        </Typography>
                      </Box>
                      <Chip size="small" label={`${roles.length} selected`} sx={{ fontWeight: 800 }} />
                    </Box>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      {roles.length ? roles.map((group) => (
                        <Chip
                          key={group.id || group.name}
                          label={group.name}
                          size="small"
                          sx={{ ...getRoleChipSx(group.name, isDark), height: 24, fontWeight: 800 }}
                        />
                      )) : (
                        <Typography sx={{ color: mutedText, fontSize: '0.9rem' }}>No role assigned.</Typography>
                      )}
                    </Stack>
                  </Paper>
                </Box>
              );
            })()
          ) : (
            // Add/Edit Mode
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  name="user_username"
                  value={formData.username}
                  onChange={handleInputChange}
                  error={!!formErrors.username}
                  helperText={
                    formErrors.username
                    || (drawerMode === 'edit' ? 'Username cannot be changed after user creation.' : '')
                  }
                  required
                  disabled={drawerMode === 'edit'}
                    autoComplete="new-password"
                    data-form-type="other"
                    inputProps={{
                      autoComplete: 'off',
                      'data-lpignore': 'true',
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent !important',
                        '& .MuiOutlinedInput-input': {
                          backgroundColor: 'transparent !important',
                          '&:-webkit-autofill': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            WebkitTextFillColor: isDark ? '#ddd' : '#212121',
                            caretColor: isDark ? '#ddd' : '#212121',
                            transition: 'background-color 5000s ease-in-out 0s',
                            backgroundColor: 'transparent !important',
                          },
                          '&:-webkit-autofill:hover': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            backgroundColor: 'transparent !important',
                          },
                          '&:-webkit-autofill:focus': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            backgroundColor: 'transparent !important',
                          },
                          '&:-webkit-autofill:active': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            backgroundColor: 'transparent !important',
                          },
                        },
                        '& fieldset': {
                          borderColor: isDark ? '#333246' : '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: isDark ? '#555' : '#999',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: THEME_COLOR,
                        },
                      },
                    }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  required
                  autoComplete="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: isDark ? '#999' : '#757575' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent !important',
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: 'transparent !important',
                      },
                      '& fieldset': {
                        borderColor: isDark ? '#333246' : '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? '#555' : '#999',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: THEME_COLOR,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent !important',
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: 'transparent !important',
                      },
                      '& fieldset': {
                        borderColor: isDark ? '#333246' : '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? '#555' : '#999',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: THEME_COLOR,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent !important',
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: 'transparent !important',
                      },
                      '& fieldset': {
                        borderColor: isDark ? '#333246' : '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? '#555' : '#999',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: THEME_COLOR,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={drawerMode === 'edit' ? 6 : 12}>
                <TextField
                  fullWidth
                  label={drawerMode === 'add' ? 'Password' : 'New Password'}
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={!!formErrors.password}
                  helperText={formErrors.password || (drawerMode === 'edit' ? 'Leave blank to keep current password.' : '')}
                  required={drawerMode === 'add'}
                  autoComplete={drawerMode === 'add' ? 'new-password' : 'off'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent !important',
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: 'transparent !important',
                      },
                      '& fieldset': {
                        borderColor: isDark ? '#333246' : '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? '#555' : '#999',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: THEME_COLOR,
                      },
                    },
                  }}
                />
              </Grid>
              {drawerMode === 'add' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    name="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    error={!!formErrors.confirm_password}
                    helperText={formErrors.confirm_password}
                    required
                    autoComplete="new-password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent !important',
                        '& .MuiOutlinedInput-input': {
                          backgroundColor: 'transparent !important',
                          '&:-webkit-autofill': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            WebkitTextFillColor: isDark ? '#ddd' : '#212121',
                            caretColor: isDark ? '#ddd' : '#212121',
                            transition: 'background-color 5000s ease-in-out 0s',
                            backgroundColor: 'transparent !important',
                          },
                          '&:-webkit-autofill:hover': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            backgroundColor: 'transparent !important',
                          },
                          '&:-webkit-autofill:focus': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            backgroundColor: 'transparent !important',
                          },
                          '&:-webkit-autofill:active': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            backgroundColor: 'transparent !important',
                          },
                        },
                        '& fieldset': {
                          borderColor: isDark ? '#333246' : '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: isDark ? '#555' : '#999',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: THEME_COLOR,
                        },
                      },
                    }}
                  />
                </Grid>
              )}
              {drawerMode === 'edit' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    error={!!formErrors.confirm_password}
                    helperText={formErrors.confirm_password || 'Required only when changing password.'}
                    required={!!formData.password}
                    disabled={!formData.password}
                    autoComplete="new-password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent !important',
                        '& .MuiOutlinedInput-input': {
                          backgroundColor: 'transparent !important',
                          '&:-webkit-autofill': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            WebkitTextFillColor: isDark ? '#ddd' : '#212121',
                            caretColor: isDark ? '#ddd' : '#212121',
                            transition: 'background-color 5000s ease-in-out 0s',
                            backgroundColor: 'transparent !important',
                          },
                          '&:-webkit-autofill:hover': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            backgroundColor: 'transparent !important',
                          },
                          '&:-webkit-autofill:focus': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            backgroundColor: 'transparent !important',
                          },
                          '&:-webkit-autofill:active': {
                            WebkitBoxShadow: isDark ? '0 0 0 100px #2a2a31 inset !important' : '0 0 0 100px transparent inset !important',
                            backgroundColor: 'transparent !important',
                          },
                        },
                        '& fieldset': {
                          borderColor: isDark ? '#333246' : '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: isDark ? '#555' : '#999',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: THEME_COLOR,
                        },
                      },
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!formErrors.hotel_ids}>
                  <InputLabel
                    sx={{
                      color: isDark ? '#999' : '#757575',
                    }}
                  >
                    Hotels *
                  </InputLabel>
                  <Select
                    name="hotel_ids"
                    multiple
                    value={formData.hotel_ids || []}
                    onChange={(e) => {
                      const value = e.target.value;
                      const selectedIds = typeof value === 'string' ? value.split(',').map(id => parseInt(id)) : value.map(id => parseInt(id));
                      setFormData((prev) => ({
                        ...prev,
                        hotel_ids: selectedIds,
                      }));
                      // Clear error when user selects hotels
                      if (formErrors.hotel_ids) {
                        setFormErrors((prev) => ({
                          ...prev,
                          hotel_ids: '',
                        }));
                      }
                    }}
                    label="Hotels *"
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) return 'Select hotels';
                      if (selected.length === 1) {
                        const hotel = hotels.find(h => h.id === selected[0]);
                        return hotel ? hotel.name : '1 hotel selected';
                      }
                      return `${selected.length} hotels selected`;
                    }}
                    sx={{
                      color: isDark ? '#ddd' : '#212121',
                      backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: formErrors.hotel_ids 
                          ? '#d32f2f' 
                          : (isDark ? '#333246' : '#e0e0e0'),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: formErrors.hotel_ids 
                          ? '#d32f2f' 
                          : (isDark ? '#555' : '#999'),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: formErrors.hotel_ids 
                          ? '#d32f2f' 
                          : THEME_COLOR,
                      },
                    }}
                    MenuProps={{
                      ...drawerOverlays.selectMenuProps,
                      PaperProps: {
                        ...drawerOverlays.selectMenuProps.PaperProps,
                        sx: {
                          ...drawerOverlays.selectMenuProps.PaperProps?.sx,
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {hotels
                      .filter(hotel => {
                        // Only show hotels from current user's organisation
                        const orgId = formData.organisation_id || currentUserOrg?.id;
                        if (orgId) {
                          return hotel.organisation_id === parseInt(orgId);
                        }
                        return false;
                      })
                      .sort((a, b) => {
                        // Sort hotels alphabetically
                        const nameA = (a.name || '').toLowerCase();
                        const nameB = (b.name || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((hotel) => (
                        <MenuItem key={hotel.id} value={hotel.id}>
                          <Checkbox
                            checked={(formData.hotel_ids || []).includes(hotel.id)}
                            sx={{
                              color: isDark ? '#999' : '#757575',
                              '&.Mui-checked': {
                                color: THEME_COLOR,
                              },
                            }}
                          />
                          <ListItemText primary={hotel.name} />
                        </MenuItem>
                      ))}
                  </Select>
                  {formErrors.hotel_ids && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.75 }}>
                      {formErrors.hotel_ids}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!formErrors.group_ids}>
                  <InputLabel
                    sx={{
                      color: isDark ? '#999' : '#757575',
                    }}
                  >
                    Role
                  </InputLabel>
                  <Select
                    name="group_ids"
                    value={formData.use_custom_access ? 'custom' : ((formData.group_ids || [])[0] || '')}
                    disabled={isEditingOwnOwnerRole}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setFormData((prev) => ({
                          ...prev,
                          use_custom_access: true,
                          group_ids: [],
                        }));
                        setFormErrors((prev) => ({
                          ...prev,
                          group_ids: '',
                        }));
                        return;
                      }
                      const selectedId = parseInt(e.target.value, 10);
                      setFormData((prev) => ({
                        ...prev,
                        use_custom_access: false,
                        group_ids: Number.isFinite(selectedId) ? [selectedId] : [],
                      }));
                      if (formErrors.group_ids) {
                        setFormErrors((prev) => ({
                          ...prev,
                          group_ids: '',
                        }));
                      }
                    }}
                    label="Role"
                    MenuProps={drawerOverlays.selectMenuProps}
                    renderValue={(selected) => {
                      if (!selected) return 'Select role';
                      if (selected === 'custom') return 'Custom Access';
                      const selectedRole = groups.find(g => g.id === selected);
                      return selectedRole ? selectedRole.name : 'Select role';
                    }}
                    sx={{
                      color: isDark ? '#ddd' : '#212121',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: formErrors.group_ids 
                          ? '#d32f2f' 
                          : (isDark ? '#333246' : '#e0e0e0'),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: formErrors.group_ids 
                          ? '#d32f2f' 
                          : (isDark ? '#555' : '#999'),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: formErrors.group_ids 
                          ? '#d32f2f' 
                          : THEME_COLOR,
                      },
                    }}
                  >
                    <MenuItem value="custom">
                      Custom Access
                    </MenuItem>
                    {groups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.group_ids && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.75 }}>
                      {formErrors.group_ids}
                    </Typography>
                  )}
                  {!formErrors.group_ids && (
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 0.5, ml: 1.75 }}>
                      {isEditingOwnOwnerRole
                        ? 'Owner role is locked for your own account to prevent losing user management access.'
                        : formData.use_custom_access
                          ? 'Custom Access uses only the permissions selected below.'
                          : 'Role provides the user&apos;s default permissions.'}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontSize: '0.68rem',
                      color: isDark ? '#8a939e' : '#78828c',
                    }}
                  >
                    Additional Access ({(formData.feature_permission_codes || []).filter((code) => !selectedRolePermissionSet.has(code)).length} selected)
                  </Typography>
                  <Typography sx={{ mb: 1.25, color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.76rem', lineHeight: 1.45 }}>
                    Permissions not included in the selected role. Hotel-package features stay limited to what the hotel has purchased; org-level access (for example Subscription) can still be granted here.
                  </Typography>
                  <Box
                    sx={{
                      borderRadius: '16px',
                      border: `1px solid ${formErrors.feature_permission_codes ? '#f44336' : (isDark ? 'rgba(148,163,184,0.18)' : 'rgba(15,23,42,0.1)')}`,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.025)',
                      maxHeight: 360,
                      overflowY: 'auto',
                      p: 1,
                    }}
                  >
                    {(!formData.hotel_ids || formData.hotel_ids.length === 0) ? (
                      <Typography sx={{ p: 1.5, color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.82rem' }}>
                        Select hotel(s) first to load assignable additional access.
                      </Typography>
                    ) : Object.keys(groupedFeaturePermissions).length === 0 ? (
                      <Typography sx={{ p: 1.5, color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.82rem' }}>
                        No missing permissions for this role and hotel selection. The role already covers assignable access.
                      </Typography>
                    ) : Object.entries(groupedFeaturePermissions).map(([appLabel, permissions]) => (
                      <Box key={`${appLabel}-feature-panel`} sx={{ mb: 1.2, '&:last-of-type': { mb: 0 } }}>
                        <Typography sx={{ px: 1, py: 0.75, color: isDark ? '#d1d5db' : '#334155', fontWeight: 800, fontSize: '0.78rem' }}>
                          {appLabel}
                        </Typography>
                        <Box sx={{ display: 'grid', gap: 0.75 }}>
                          {permissions.map((permission) => {
                            const checked = (formData.feature_permission_codes || []).includes(permission.code);
                            const dependencyDisabled = !checked
                              && isPmsDependentAdditionalAccess(permission.code)
                              && !hasPmsBaseAccess;
                            const togglePermission = () => {
                              if (dependencyDisabled) {
                                setFormErrors((prev) => ({
                                  ...prev,
                                  feature_permission_codes: 'Select Booking Operations first to assign PMS-dependent access.',
                                }));
                                return;
                              }
                              const current = formData.feature_permission_codes || [];
                              const dependentSelected = current.some((code) => isPmsDependentAdditionalAccess(code));
                              if (
                                checked
                                && permission.code === PMS_BASE_PERMISSION_CODE
                                && !selectedRolePermissionSet.has(PMS_BASE_PERMISSION_CODE)
                                && dependentSelected
                              ) {
                                setFormErrors((prev) => ({
                                  ...prev,
                                  feature_permission_codes: 'Remove PMS-dependent access before removing Booking Operations.',
                                }));
                                return;
                              }
                              const next = checked
                                ? current.filter((code) => code !== permission.code)
                                : [...current, permission.code];
                              setFormData((prev) => ({ ...prev, feature_permission_codes: next }));
                              if (formErrors.feature_permission_codes) {
                                setFormErrors((prev) => ({ ...prev, feature_permission_codes: '' }));
                              }
                            };
                            return (
                              <Box
                                key={permission.code}
                                onClick={togglePermission}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  p: 1,
                                  borderRadius: '12px',
                                  cursor: dependencyDisabled ? 'not-allowed' : 'pointer',
                                  opacity: dependencyDisabled ? 0.48 : 1,
                                  border: `1px solid ${checked ? THEME_COLOR : 'transparent'}`,
                                  backgroundColor: checked
                                    ? (isDark ? 'rgba(76,225,182,0.16)' : 'rgba(76,225,182,0.09)')
                                    : (isDark ? 'rgba(255,255,255,0.025)' : 'rgba(255,255,255,0.7)'),
                                  '&:hover': {
                                    borderColor: checked ? THEME_COLOR : (isDark ? 'rgba(148,163,184,0.22)' : 'rgba(15,23,42,0.12)'),
                                    backgroundColor: checked
                                      ? (isDark ? 'rgba(76,225,182,0.22)' : 'rgba(76,225,182,0.12)')
                                      : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)'),
                                  },
                                }}
                              >
                                <Checkbox
                                  checked={checked}
                                  disabled={dependencyDisabled}
                                  sx={{
                                    p: 0.25,
                                    color: isDark ? '#9ca3af' : '#64748b',
                                    '&.Mui-checked': { color: THEME_COLOR },
                                  }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ color: isDark ? '#e5e7eb' : '#0f172a', fontWeight: 700, fontSize: '0.86rem' }}>
                                    {getPermissionDisplayName(permission)}
                                  </Typography>
                                  <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.72rem', wordBreak: 'break-word' }}>
                                    {dependencyDisabled ? 'Requires Booking Operations access' : permission.code}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  {formErrors.feature_permission_codes && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.75 }}>
                      {Array.isArray(formErrors.feature_permission_codes)
                        ? formErrors.feature_permission_codes.join(', ')
                        : formErrors.feature_permission_codes}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Box
                  onClick={() => {
                    handleInputChange({
                      target: {
                        name: 'is_active',
                        type: 'checkbox',
                        checked: !formData.is_active,
                      },
                    });
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: '14px',
                    cursor: 'pointer',
                    border: `1.5px solid ${formData.is_active ? THEME_COLOR : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')}`,
                    backgroundColor: formData.is_active
                      ? (isDark ? 'rgba(76, 225, 182, 0.14)' : 'rgba(76, 225, 182, 0.07)')
                      : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                    transition: 'all 0.2s ease',
                  }}
                >
                  <PersonIcon sx={{ fontSize: 22, color: formData.is_active ? THEME_COLOR : (isDark ? '#888' : '#9e9e9e') }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: formData.is_active ? THEME_COLOR : (isDark ? '#ddd' : '#424242'),
                      }}
                    >
                      Active user access
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: isDark ? '#8a8a8a' : '#757575', lineHeight: 1.4 }}>
                      Controls whether this user can sign in and access assigned hotels.
                    </Typography>
                  </Box>
                  <Switch
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    onClick={(e) => e.stopPropagation()}
                    name="is_active"
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: THEME_COLOR,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: THEME_COLOR,
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  onClick={() => {
                    handleInputChange({
                      target: {
                        name: 'login_email_otp_enabled',
                        type: 'checkbox',
                        checked: !formData.login_email_otp_enabled,
                      },
                    });
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: '14px',
                    cursor: 'pointer',
                    border: `1.5px solid ${formData.login_email_otp_enabled ? THEME_COLOR : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')}`,
                    backgroundColor: formData.login_email_otp_enabled
                      ? (isDark ? 'rgba(76, 225, 182, 0.14)' : 'rgba(76, 225, 182, 0.07)')
                      : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                    transition: 'all 0.2s ease',
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 22, color: formData.login_email_otp_enabled ? THEME_COLOR : (isDark ? '#888' : '#9e9e9e') }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: formData.login_email_otp_enabled ? THEME_COLOR : (isDark ? '#ddd' : '#424242'),
                      }}
                    >
                      Login email OTP
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: isDark ? '#8a8a8a' : '#757575', lineHeight: 1.4 }}>
                      Require email OTP after password login for this user.
                    </Typography>
                  </Box>
                  <Switch
                    checked={Boolean(formData.login_email_otp_enabled)}
                    onChange={handleInputChange}
                    onClick={(e) => e.stopPropagation()}
                    name="login_email_otp_enabled"
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: THEME_COLOR,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: THEME_COLOR,
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Footer */}
        {drawerMode !== 'view' ? (
          <Box
            sx={{
              p: 1.5,
              ...getHousekeepingDrawerFooterSx(isDark),
              flexShrink: 0,
              backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
              backgroundImage: 'none',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseDrawer}
                  disabled={saving}
                  sx={getDrawerCancelButtonSx(isDark, themeColors.surfaceBorder)}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    ...getDrawerPrimaryButtonSx(),
                    '&.Mui-disabled': {
                      background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                      color: isDark ? '#777' : '#999',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {saving ? <CircularProgress size={20} /> : drawerMode === 'add' ? 'Create' : 'Save'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              p: 1.5,
              ...getHousekeepingDrawerFooterSx(isDark),
              flexShrink: 0,
              backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
              backgroundImage: 'none',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseDrawer}
                  sx={getDrawerCancelButtonSx(isDark, themeColors.surfaceBorder)}
                >
                  Close
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        </Box>
      </Drawer>

      {/* Role Management Drawer */}
      <Drawer
        anchor="right"
        open={rolesDialogOpen}
        onClose={handleCloseRolesDialog}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 1080 },
            maxWidth: '100vw',
            backgroundColor: isDark ? '#2a2a31' : '#ffffff',
            borderLeft: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              color: isDark ? '#fff' : '#212121',
              borderBottom: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon sx={{ color: THEME_COLOR }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121' }}>
                Role & Permission Management
              </Typography>
            </Box>
            <IconButton onClick={handleCloseRolesDialog} sx={{ color: isDark ? '#bbb' : '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant={rolesDialogView === 'roles' ? 'contained' : 'outlined'}
              onClick={() => handleRolesDialogViewChange('roles')}
              sx={{
                textTransform: 'none',
                backgroundColor: rolesDialogView === 'roles' ? THEME_COLOR : 'transparent',
                color: rolesDialogView === 'roles' ? '#000' : THEME_COLOR,
                borderColor: THEME_COLOR,
                '&:hover': {
                  borderColor: THEME_COLOR,
                  backgroundColor:
                    rolesDialogView === 'roles'
                      ? THEME_COLOR
                      : isDark
                        ? 'rgba(76, 225, 182, 0.1)'
                        : 'rgba(76, 225, 182, 0.05)',
                },
              }}
            >
              Roles & Permissions
            </Button>
            <Button
              variant={rolesDialogView === 'audit' ? 'contained' : 'outlined'}
              onClick={() => handleRolesDialogViewChange('audit')}
              sx={{
                textTransform: 'none',
                backgroundColor: rolesDialogView === 'audit' ? THEME_COLOR : 'transparent',
                color: rolesDialogView === 'audit' ? '#000' : THEME_COLOR,
                borderColor: THEME_COLOR,
                '&:hover': {
                  borderColor: THEME_COLOR,
                  backgroundColor:
                    rolesDialogView === 'audit'
                      ? THEME_COLOR
                      : isDark
                        ? 'rgba(76, 225, 182, 0.1)'
                        : 'rgba(76, 225, 182, 0.05)',
                },
              }}
            >
              Audit Logs
            </Button>
          </Box>

          {rolesDialogView === 'roles' ? (
            rolesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress sx={{ color: THEME_COLOR }} />
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderColor: isDark ? '#333246' : '#e0e0e0',
                      backgroundColor: isDark ? '#2a2a31' : '#fff',
                      maxHeight: 420,
                      overflowY: 'auto',
                    }}
                  >
                    <Box sx={{ p: 1.5, borderBottom: `1px solid ${isDark ? '#333246' : '#e0e0e0'}` }}>
                      <Typography variant="subtitle2" sx={{ color: isDark ? '#ddd' : '#212121' }}>
                        Roles
                      </Typography>
                    </Box>
                    {rolesList.map((role) => (
                      <Box
                        key={role.id}
                        onClick={() => handleRoleSelect(role)}
                        sx={{
                          cursor: 'pointer',
                          p: 1.5,
                          borderBottom: `1px solid ${isDark ? '#333246' : '#f0f0f0'}`,
                          backgroundColor: roleForm.id === role.id
                            ? (isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)')
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: isDark ? '#ddd' : '#212121' }}
                        >
                          {role.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: isDark ? '#999' : '#757575' }}
                        >
                          {role.permission_codes?.length || 0} permissions
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Role Name"
                        value={roleForm.name}
                        onChange={(event) => {
                          setRoleForm((prev) => ({ ...prev, name: event.target.value }));
                          if (roleFormErrors.name) {
                            setRoleFormErrors((prev) => ({ ...prev, name: '' }));
                          }
                        }}
                        error={!!roleFormErrors.name}
                        helperText={Array.isArray(roleFormErrors.name) ? roleFormErrors.name.join(', ') : roleFormErrors.name}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Permissions</InputLabel>
                        <Select
                          multiple
                          value={roleForm.permission_codes}
                          onChange={(event) => {
                            const value = event.target.value;
                            setRoleForm((prev) => ({
                              ...prev,
                              permission_codes: Array.isArray(value) ? value : [],
                            }));
                            if (roleFormErrors.permission_codes) {
                              setRoleFormErrors((prev) => ({ ...prev, permission_codes: '' }));
                            }
                          }}
                          label="Permissions"
                          renderValue={(selected) => {
                            if (!selected || selected.length === 0) return 'No permissions selected';
                            return `${selected.length} permissions selected`;
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 420,
                              },
                            },
                          }}
                        >
                          {Object.entries(groupedPermissions).map(([appLabel, permissions]) => ([
                            <ListSubheader key={`${appLabel}-header`}>
                              {appLabel}
                            </ListSubheader>,
                            ...permissions.map((permission) => (
                              <MenuItem key={permission.code} value={permission.code}>
                                <Checkbox checked={roleForm.permission_codes.includes(permission.code)} />
                                <ListItemText
                                  primary={getPermissionDisplayName(permission)}
                                  secondary={permission.code}
                                />
                              </MenuItem>
                            )),
                          ]))}
                        </Select>
                      </FormControl>
                      {roleFormErrors.permission_codes && (
                        <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
                          {Array.isArray(roleFormErrors.permission_codes)
                            ? roleFormErrors.permission_codes.join(', ')
                            : roleFormErrors.permission_codes}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Event Type</InputLabel>
                  <Select
                    label="Event Type"
                    value={auditEventTypeFilter}
                    onChange={(event) => setAuditEventTypeFilter(event.target.value)}
                  >
                    <MenuItem value="">All events</MenuItem>
                    {AUDIT_EVENT_TYPES.map((eventType) => (
                      <MenuItem key={eventType} value={eventType}>
                        {eventType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Target User ID"
                  value={auditTargetUserIdFilter}
                  onChange={(event) => setAuditTargetUserIdFilter(event.target.value.replace(/\D/g, ''))}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Button variant="outlined" onClick={handleAuditResetFilters} disabled={auditLogsLoading}>
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAuditApplyFilters}
                    disabled={auditLogsLoading}
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: '#000',
                      '&:hover': {
                        backgroundColor: THEME_COLOR,
                        opacity: 0.9,
                      },
                    }}
                  >
                    Apply Filters
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    borderColor: isDark ? '#333246' : '#e0e0e0',
                    backgroundColor: isDark ? '#2a2a31' : '#fff',
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditLogsLoading ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                              <CircularProgress size={24} sx={{ color: THEME_COLOR }} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : auditLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', py: 1 }}>
                              No RBAC audit logs found for the selected filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        auditLogs.map((log) => (
                          <TableRow
                            key={log.id}
                            hover
                            onClick={() => handleOpenAuditDetail(log)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              {formatAuditTimestamp(log.created_at)}
                            </TableCell>
                            <TableCell>{log.event_type || '-'}</TableCell>
                            <TableCell>{log.action || '-'}</TableCell>
                            <TableCell>{log.actor?.username || '-'}</TableCell>
                            <TableCell>
                              {log.target_user?.username || log.target_role?.name || '-'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={log.status || 'unknown'}
                                color={log.status === 'success' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="View audit details">
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenAuditDetail(log);
                                  }}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={auditPagination.total_records || 0}
                  page={Math.max((auditPagination.page || 1) - 1, 0)}
                  onPageChange={handleAuditPageChange}
                  rowsPerPage={auditPagination.per_page || DEFAULT_AUDIT_PER_PAGE}
                  onRowsPerPageChange={handleAuditRowsPerPageChange}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </Grid>
            </Grid>
          )}
          </Box>
          <Box
            sx={{
              borderTop: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              p: 2,
              justifyContent: 'space-between',
              display: 'flex',
            }}
          >
          {rolesDialogView === 'roles' ? (
            <>
              <Box>
                <Button
                  variant="text"
                  onClick={resetRoleForm}
                  disabled={savingRole || rolesLoading}
                  sx={{ color: isDark ? '#999' : '#666' }}
                >
                  New Role
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {roleForm.id && (
                  <Tooltip title={isOwnerRoleName(roleForm.name) ? OWNER_ROLE_SUPPORT_MESSAGE : 'Delete role'}>
                    <span>
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={handleRoleDelete}
                        disabled={savingRole || deletingRoleId === roleForm.id || isOwnerRoleName(roleForm.name)}
                      >
                        {deletingRoleId === roleForm.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Button
                  variant="outlined"
                  onClick={handleCloseRolesDialog}
                  disabled={savingRole}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRoleSave}
                  disabled={savingRole}
                  sx={{
                    backgroundColor: THEME_COLOR,
                    color: '#000',
                    '&:hover': {
                      backgroundColor: THEME_COLOR,
                      opacity: 0.9,
                    },
                  }}
                >
                  {savingRole ? 'Saving...' : roleForm.id ? 'Update Role' : 'Create Role'}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => fetchRbacAuditLogs({ pageValue: auditPagination.page || 1 })}
                  disabled={auditLogsLoading}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCloseRolesDialog}
                  disabled={auditLogsLoading}
                >
                  Close
                </Button>
              </Box>
            </>
          )}
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={auditDetailDrawerOpen}
        onClose={handleCloseAuditDetail}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 560 },
            backgroundColor: isDark ? '#1f1f27' : '#fafafa',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              background: isDark
                ? 'linear-gradient(120deg, rgba(76,225,182,0.16), rgba(123,31,162,0.18))'
                : 'linear-gradient(120deg, rgba(76,225,182,0.18), rgba(123,31,162,0.10))',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FactCheckIcon sx={{ color: THEME_COLOR, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? '#e8e8ee' : '#222', fontWeight: 700 }}>
                RBAC Audit Detail
              </Typography>
              {selectedAuditLog?.id && (
                <Chip
                  size="small"
                  label={`#${selectedAuditLog.id}`}
                  sx={{
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.2)',
                    color: THEME_COLOR,
                    fontWeight: 700,
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleCloseAuditDetail} sx={{ color: isDark ? '#bbb' : '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 2, borderBottom: `1px solid ${isDark ? '#2f2f3a' : '#ececec'}` }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`Event: ${(selectedAuditLog?.event_type || '-').replace(/_/g, ' ')}`}
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  textTransform: 'capitalize',
                  backgroundColor: isDark ? 'rgba(123,31,162,0.18)' : 'rgba(123,31,162,0.08)',
                  color: isDark ? '#ce93d8' : '#6a1b9a',
                }}
              />
              <Chip
                size="small"
                label={`Status: ${selectedAuditLog?.status || 'unknown'}`}
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  textTransform: 'capitalize',
                  backgroundColor:
                    selectedAuditLog?.status === 'success'
                      ? (isDark ? 'rgba(76,175,80,0.22)' : 'rgba(76,175,80,0.12)')
                      : (isDark ? 'rgba(158,158,158,0.22)' : 'rgba(158,158,158,0.18)'),
                  color: selectedAuditLog?.status === 'success' ? (isDark ? '#a5d6a7' : '#2e7d32') : (isDark ? '#ddd' : '#555'),
                }}
              />
              <Chip
                size="small"
                label={`At: ${formatAuditTimestamp(selectedAuditLog?.created_at)}`}
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  backgroundColor: isDark ? 'rgba(76,225,182,0.18)' : 'rgba(76,225,182,0.12)',
                  color: THEME_COLOR,
                }}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${isDark ? '#343442' : '#e6e6f0'}`,
                backgroundColor: isDark ? '#272733' : '#fff',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: isDark ? '#f0f0f5' : '#20232a', fontWeight: 700, mb: 1 }}>
                Actor & Targets
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                    Actor
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#d7d7df' : '#454c57' }}>
                    {selectedAuditLog?.actor?.username || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                    Target User
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#d7d7df' : '#454c57' }}>
                    {selectedAuditLog?.target_user?.username || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                    Target Role
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#d7d7df' : '#454c57' }}>
                    {selectedAuditLog?.target_role?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                    Action
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#d7d7df' : '#454c57' }}>
                    {selectedAuditLog?.action || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${isDark ? '#343442' : '#e6e6f0'}`,
                backgroundColor: isDark ? '#272733' : '#fff',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: isDark ? '#f0f0f5' : '#20232a', fontWeight: 700, mb: 1 }}>
                Request Context
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                    Path
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#d7d7df' : '#454c57' }}>
                    {selectedAuditLog?.request?.path || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                    Method
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#d7d7df' : '#454c57' }}>
                    {selectedAuditLog?.request?.method || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
                    IP Address
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#d7d7df' : '#454c57' }}>
                    {selectedAuditLog?.request?.ip_address || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${isDark ? '#343442' : '#e6e6f0'}`,
                    backgroundColor: isDark ? '#272733' : '#fff',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: isDark ? '#f0f0f5' : '#20232a', fontWeight: 700 }}>
                      Before State
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleCopyAuditPayload('Before state', selectedAuditLog?.before_state)}
                      sx={{ textTransform: 'none', minWidth: 'auto', color: THEME_COLOR, px: 0.5 }}
                    >
                      Copy
                    </Button>
                  </Box>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.25,
                      borderRadius: 1.5,
                      overflowX: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.72rem',
                      lineHeight: 1.45,
                      border: `1px dashed ${isDark ? '#4b4b57' : '#d9d9d9'}`,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
                      color: isDark ? '#d7d7df' : '#454c57',
                    }}
                  >
                    {stringifyAuditPayload(selectedAuditLog?.before_state)}
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${isDark ? '#343442' : '#e6e6f0'}`,
                    backgroundColor: isDark ? '#272733' : '#fff',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: isDark ? '#f0f0f5' : '#20232a', fontWeight: 700 }}>
                      After State
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleCopyAuditPayload('After state', selectedAuditLog?.after_state)}
                      sx={{ textTransform: 'none', minWidth: 'auto', color: THEME_COLOR, px: 0.5 }}
                    >
                      Copy
                    </Button>
                  </Box>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.25,
                      borderRadius: 1.5,
                      overflowX: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.72rem',
                      lineHeight: 1.45,
                      border: `1px dashed ${isDark ? '#4b4b57' : '#d9d9d9'}`,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
                      color: isDark ? '#d7d7df' : '#454c57',
                    }}
                  >
                    {stringifyAuditPayload(selectedAuditLog?.after_state)}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${isDark ? '#343442' : '#e6e6f0'}`,
                backgroundColor: isDark ? '#272733' : '#fff',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: isDark ? '#f0f0f5' : '#20232a', fontWeight: 700 }}>
                  Metadata
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                  onClick={() => handleCopyAuditPayload('Metadata', selectedAuditLog?.metadata)}
                  sx={{ textTransform: 'none', minWidth: 'auto', color: THEME_COLOR, px: 0.5 }}
                >
                  Copy
                </Button>
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.25,
                  borderRadius: 1.5,
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '0.72rem',
                  lineHeight: 1.45,
                  border: `1px dashed ${isDark ? '#4b4b57' : '#d9d9d9'}`,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
                  color: isDark ? '#d7d7df' : '#454c57',
                }}
              >
                {stringifyAuditPayload(selectedAuditLog?.metadata)}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2a2a31' : '#ffffff',
            border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: isDark ? '#fff' : '#212121',
            borderBottom: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
          }}
        >
          Delete User
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText
            sx={{
              color: isDark ? '#ddd' : '#212121',
            }}
          >
            Are you sure you want to delete the user "{userToDelete?.username}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
            p: 2,
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              color: isDark ? '#999' : '#757575',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            sx={{
              '&.Mui-disabled': {
                bgcolor: isDark ? '#555' : '#ccc',
              },
            }}
          >
            {deleting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: '#fff' }} />
                Deleting...
              </Box>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LandingUsers;
